/**
 * MetadataEditor — in-app create/edit for metadata sheets.
 *
 * Operates in two modes based on URL params:
 *   Create mode: /projects/:projectId/metadata/new
 *     - Pre-populates with DEFAULT_COL_VALUES (barcode + sample_id).
 *     - Calls metadataAPI.createSheet on save.
 *
 *   Edit mode:   /projects/:projectId/metadata/:sheetId/edit
 *     - Fetches the existing sheet and loads its columns/rows.
 *     - Calls metadataAPI.updateSheet on save.
 *
 * Column keys use a random UUID while editing so React can track identity
 * across renames without index-based key collisions.
 */

import { metadataAPI, projectAPI } from "../services/api";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ConfirmModal } from "../components/ui/confirm-modal";

export default function MetadataEditor() {
    const { projectId, sheetId } = useParams();
    const navigate = useNavigate();

    const [cols, setCols] = useState([]);
    const [rows, setRows] = useState([]);
    const [name, setName] = useState('');
    const [type, setType] = useState('batch');   // "batch" | "master"
    const [batchIndex, setBatchIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
    const [bulkRowCount, setBulkRowCount] = useState('1');
    // Barcodes detected in the uploaded zip for this batch — used for validation.
    // null = not yet fetched; [] = fetched but no files uploaded; [...] = files found.
    const [detectedBarcodes, setDetectedBarcodes] = useState(null);
    const [barcodesError, setBarcodesError] = useState(false);

    // Standard columns the pipeline expects. barcode + sample_id are required (undeletable).
    // All others are optional — the user can delete what they don't need.
    const DEFAULT_COL_VALUES = [
        { key: 'barcode',     label: 'barcode',     type: 'string', required: true  },
        { key: 'sample_id',   label: 'sample_id',   type: 'string', required: true  },
        { key: 'folder',      label: 'folder',      type: 'string', required: false },
        { key: 'sample_info', label: 'sample_info', type: 'string', required: false },
        { key: 'sample_type', label: 'sample_type', type: 'string', required: false },
        { key: 'date',        label: 'date',        type: 'string', required: false },
        { key: 'submission',  label: 'submission',  type: 'string', required: false },
        { key: 'hatchery',    label: 'hatchery',    type: 'string', required: false },
        { key: 'sum',         label: 'sum',         type: 'string', required: false },
        { key: 'status',      label: 'status',      type: 'string', required: false },
        { key: 'vis_score',   label: 'vis_score',   type: 'string', required: false },
        { key: 'sig_lesion',  label: 'sig_lesion',  type: 'string', required: false },
        { key: 'weight',      label: 'weight',      type: 'string', required: false },
        { key: 'length',      label: 'length',      type: 'string', required: false },
    ];

    // Fetch detected barcodes whenever this is a batch sheet with a known batch number.
    // Skipped for standalone sheets (no project directory to read from).
    // Runs on mount for edit mode, and whenever batchIndex changes in create mode.
    useEffect(() => {
        if (type !== 'batch' || !batchIndex || !projectId) return;
        setDetectedBarcodes(null);
        setBarcodesError(false);
        projectAPI.getBatchBarcodes(projectId, batchIndex)
            .then(res => setDetectedBarcodes(res.data.barcodes))
            .catch(() => setBarcodesError(true));
    }, [type, batchIndex, projectId]);

    useEffect(() => {
        if (sheetId) {
            // Edit mode — fetch the existing sheet from the API.
            const fetchSheet = async () => {
                try {
                    const res = await metadataAPI.getSheet(sheetId);
                    setName(res.data.name);
                    setType(res.data.type);
                    setBatchIndex(res.data.batch_index);
                    setCols(res.data.columns);
                    setRows(res.data.rows);
                } catch (err) {
                    setError(err.response?.data?.detail || `Failed to load sheet data for sheet id ${sheetId}`)
                }
            }
            fetchSheet();
        } else {
            // Create mode — start with the default columns and one blank row.
            setCols(DEFAULT_COL_VALUES);
            const blankRow = {};
            DEFAULT_COL_VALUES.forEach(col => { blankRow[col.key] = ''; });
            setRows([blankRow]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetId]);

    /**
     * Compare barcodes entered in the sheet against the directories detected in the
     * uploaded zip. Returns two arrays so the warning banner can describe each case:
     *   inSheetOnly  — typed in the sheet but no matching directory → rename will silently fail
     *   inFilesOnly  — directory exists but no row in the sheet → sample excluded from R analysis
     */
    const barcodeWarnings = (() => {
        if (!detectedBarcodes || type !== 'batch') return null;
        const barcodeColKey = cols.find(c => c.label === 'barcode')?.key;
        if (!barcodeColKey) return null;
        const sheetBarcodes = new Set(rows.map(r => (r[barcodeColKey] || '').trim()).filter(Boolean));
        const fileBarcodes = new Set(detectedBarcodes);
        const inSheetOnly = [...sheetBarcodes].filter(b => !fileBarcodes.has(b));
        const inFilesOnly = [...fileBarcodes].filter(b => !sheetBarcodes.has(b));
        return (inSheetOnly.length > 0 || inFilesOnly.length > 0) ? { inSheetOnly, inFilesOnly } : null;
    })();

    /**
     * Add a new optional column.
     * Date.now() is used as the column key so columns can be renamed without losing row data —
     * the key stays stable while only the label changes.
     * The same key is added to every existing row with an empty value.
     */
    const addColumn = () => {
        const newColumnKey = Date.now()
        setCols(previousCols => [...previousCols, { key: newColumnKey, label: '', type: 'string', required: false }]);
        setRows(previousRows => previousRows.map(row => ({ ...row, [newColumnKey]: '' })));
    }

    /** Remove a column by key and strip that key from every row. */
    const removeColumn = (columnKey) => {
        setCols(previousCols => previousCols.filter(column => column.key !== columnKey));
        setRows(previousRows => previousRows.map(row => {
            const rowWithoutColumn = { ...row };
            delete rowWithoutColumn[columnKey];
            return rowWithoutColumn;
        }));
    }

    /** Rename a column's display label. Does not change the key, so row data is preserved. */
    const renameColumn = (columnKey, newLabel) => {
        setCols(previousCols => previousCols.map(column =>
            column.key === columnKey ? { ...column, label: newLabel } : column
        ));
    }


    /** Add a new blank row with an empty string for every current column. */
    const addRow = () => {
        const blankRow = {};
        cols.forEach(column => {
            blankRow[column.key] = '';
        });
        setRows(previousRows => [...previousRows, blankRow]);
    }

    /** Add n blank rows at once. */
    const addBulkRows = () => {
        const n = parseInt(bulkRowCount, 10);
        if (!n || n < 1) return;
        const newRows = Array.from({ length: n }, () => {
            const row = {};
            cols.forEach(col => { row[col.key] = ''; });
            return row;
        });
        setRows(prev => [...prev, ...newRows]);
        setBulkRowCount('');
    };

    /** Remove a row by index. Prevents deleting the last remaining row. */
    const removeRow = (rowIndex) => {
        if (rows.length <= 1) return;
        setRows(previousRows => previousRows.filter((_, i) => i !== rowIndex));
    };

    /** Update a single cell value. */
    const updateCell = (rowIndex, columnKey, newValue) => {
        setRows(previousRows => previousRows.map((row, index) => {
            if (index !== rowIndex) return row;
            return { ...row, [columnKey]: newValue };
        }));
    }

    /**
     * Handle paste from Excel / Google Sheets.
     * Excel copies selections as tab-separated columns and newline-separated rows.
     * We parse the pasted text and fill cells starting from (rowIndex, colIndex),
     * adding new rows automatically if the pasted block extends beyond the last row.
     */
    const handlePaste = (e, rowIndex, colIndex) => {
        const text = e.clipboardData.getData('text/plain');
        // Only intercept multi-cell pastes (contains tab or newline)
        if (!text.includes('\t') && !text.includes('\n')) return;

        e.preventDefault();

        const pastedRows = text
            .trimEnd()
            .split('\n')
            .map(line => line.split('\t'));

        setRows(previousRows => {
            const updated = previousRows.map(r => ({ ...r }));

            pastedRows.forEach((pastedRow, pasteRowOffset) => {
                const targetRowIndex = rowIndex + pasteRowOffset;

                // Add blank rows if the paste extends beyond the current grid
                while (updated.length <= targetRowIndex) {
                    const blankRow = {};
                    cols.forEach(col => { blankRow[col.key] = ''; });
                    updated.push(blankRow);
                }

                pastedRow.forEach((cellValue, pasteColOffset) => {
                    const targetColIndex = colIndex + pasteColOffset;
                    if (targetColIndex >= cols.length) return; // don't paste beyond existing columns
                    const targetColKey = cols[targetColIndex].key;
                    updated[targetRowIndex] = { ...updated[targetRowIndex], [targetColKey]: cellValue.trim() };
                });
            });

            return updated;
        });
    }

    /**
     * Saves the sheet to the API.
     * In create mode (no sheetId), calls createSheet.
     * In edit mode, calls updateSheet with the existing sheetId.
     * Navigates back to the metadata viewer on success.
     */
    const handleSave = async () => {
        // Edit mode only: validate required fields client-side (no existence check needed).
        // Create mode skips this — the backend checks existence first (409), then fields (422).
        if (sheetId) {
            const emptyLabels = cols
                .filter(c => c.required)
                .filter(col => rows.some(row => !row[col.key]?.trim()))
                .map(col => col.label);
            if (emptyLabels.length > 0) {
                setError(`All rows must have a value for: ${emptyLabels.join(' and ')}`);
                return;
            }
        }

        try {
            setSaving(true);
            setError('');

            const sheetData = {
                name,
                type,
                batch_index: batchIndex,
                columns: cols,
                rows,
            };

            if (sheetId) {
                await metadataAPI.updateSheet(sheetId, sheetData);
            } else {
                await metadataAPI.createSheet(projectId, sheetData);
            }

            navigate(`/metadata`, { state: { refresh: true } });
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save sheet');
        } finally {
            setSaving(false);
        }
    }



    return (
        <>
        <div className="flex flex-col h-screen bg-background">

            {/* Toolbar — title, form fields, and actions all in one bar */}
            <div className="flex items-end gap-3 px-4 py-3 border-b bg-card shrink-0">
                <h2 className="text-lg font-semibold whitespace-nowrap mr-2">
                    {sheetId ? 'Edit Sheet' : 'New Sheet'}
                </h2>

                <div className="flex-1">
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Sheet Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Batch 1 Metadata"
                        className="w-full text-sm border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Type</label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="text-sm border rounded-md px-3 py-2 bg-background"
                    >
                        <option value="batch">Batch</option>
                        <option value="master">Master</option>
                    </select>
                </div>

                {type === 'batch' && (
                    <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Batch Number</label>
                        <input
                            type="number"
                            value={batchIndex ?? ''}
                            onChange={(e) => setBatchIndex(Number(e.target.value))}
                            min={1}
                            className="w-32 text-sm border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                )}

                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate(`/metadata`)}>
                        Cancel
                    </Button>
                    <Button onClick={() => sheetId ? setConfirmSaveOpen(true) : handleSave()} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div className="text-sm text-destructive border-b border-destructive bg-destructive/10 px-4 py-2 shrink-0">
                    {error}
                </div>
            )}

            {/* Barcode validation banners — only relevant for batch sheets */}
            {type === 'batch' && batchIndex && barcodesError && (
                <div className="text-sm border-b border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 px-4 py-2 shrink-0 text-orange-800 dark:text-orange-200">
                    Could not check uploaded files for batch {batchIndex} — validation unavailable.
                </div>
            )}
            {type === 'batch' && batchIndex && detectedBarcodes?.length === 0 && (
                <div className="text-sm border-b border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 px-4 py-2 shrink-0 text-blue-700 dark:text-blue-300">
                    No files uploaded yet for batch {batchIndex}. Upload a zip first to validate barcodes against real files.
                </div>
            )}
            {barcodeWarnings && (
                <div className="text-sm border-b border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 px-4 py-2 shrink-0 space-y-0.5">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">Barcode mismatch detected</p>
                    {barcodeWarnings.inSheetOnly.length > 0 && (
                        <p className="text-yellow-700 dark:text-yellow-300">
                            In sheet but no matching files: <span className="font-mono">{barcodeWarnings.inSheetOnly.join(', ')}</span> — rename will silently fail
                        </p>
                    )}
                    {barcodeWarnings.inFilesOnly.length > 0 && (
                        <p className="text-yellow-700 dark:text-yellow-300">
                            Files exist but not in sheet: <span className="font-mono">{barcodeWarnings.inFilesOnly.join(', ')}</span> — these samples will be excluded from analysis
                        </p>
                    )}
                </div>
            )}

            {/* Table area — fills all remaining screen height */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 overflow-auto">
                    <table className="w-full min-w-max text-sm">

                        {/* Column headers — each label is an editable input */}
                        <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                            <tr>
                                <th className="w-10 px-3 py-2 text-center text-xs font-mono text-muted-foreground">#</th>
                                {cols.map((column) => (
                                    <th key={column.key} className="px-2 py-2 min-w-[140px] group/col">
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                value={column.label}
                                                onChange={(e) => renameColumn(column.key, e.target.value)}
                                                disabled={column.required}
                                                placeholder="Column name"
                                                className="flex-1 text-sm border rounded px-2 py-1 bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            {!column.required && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeColumn(column.key)}
                                                    title="Remove column"
                                                    className="opacity-0 group-hover/col:opacity-100 hover:text-destructive hover:bg-destructive/10"
                                                >
                                                    ✕
                                                </Button>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* Data rows */}
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-t hover:bg-muted/30 group">
                                    <td className="px-3 py-1.5 text-center text-xs font-mono text-muted-foreground">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeRow(rowIndex)}
                                            disabled={rows.length <= 1}
                                            title="Remove row"
                                            className="opacity-0 group-hover:opacity-100 h-5 w-5 p-0 hover:text-destructive hover:bg-destructive/10 disabled:opacity-0"
                                        >
                                            ✕
                                        </Button>
                                        <span className="group-hover:hidden">{rowIndex + 1}</span>
                                    </td>
                                    {cols.map((column) => (
                                        <td key={column.key} className="px-2 py-1.5">
                                            <input
                                                type="text"
                                                value={row[column.key] ?? ''}
                                                onChange={(e) => updateCell(rowIndex, column.key, e.target.value)}
                                                onPaste={(e) => handlePaste(e, rowIndex, cols.indexOf(column))}
                                                className="w-full text-sm border rounded px-2 py-1 bg-background"
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>

                        {/* Add row */}
                        <tfoot>
                            <tr className="border-t">
                                <td colSpan={cols.length + 1} className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={addRow}>
                                            + Add Row
                                        </Button>
                                        <span className="text-xs text-muted-foreground">or</span>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <span>add</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={500}
                                                value={bulkRowCount}
                                                onChange={(e) => setBulkRowCount(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && addBulkRows()}
                                                onBlur={addBulkRows}
                                                className="w-12 text-sm border rounded px-2 py-1 bg-background text-center"
                                            />
                                            <span>rows</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>

                    </table>
                </div>

                {/* Add column — pinned to the bottom of the table area */}
                <div className="border-t px-4 py-2 bg-card shrink-0">
                    <Button variant="outline" size="sm" onClick={addColumn}>
                        + Add Column
                    </Button>
                </div>
            </div>

        </div>

        <ConfirmModal
            open={confirmSaveOpen}
            title="Save changes?"
            description="This will overwrite the existing sheet. A new version will be saved in the version history."
            confirmLabel="Save"
            onConfirm={() => { setConfirmSaveOpen(false); handleSave(); }}
            onCancel={() => setConfirmSaveOpen(false)}
        />
        </>
    );

};


